"use client";

import { type ReactNode } from "react";
import { motion, type Variants } from "motion/react";

interface FeatureCardProps {
  icon: ReactNode;
  title: string;
  description: string;
  index: number;
  inView: boolean;
  variants: Variants;
}

export default function FeatureCard({
  icon,
  title,
  description,
  index,
  inView,
  variants,
}: FeatureCardProps) {
  return (
    <motion.div
      key={index}
      variants={variants}
      whileHover={{
        y: -10,
        scale: 1.03,
        transition: {
          type: "spring" as const,
          stiffness: 300,
          damping: 20,
        },
      }}
      className="group relative bg-card border-radius p-8 border border-primary-100/50 hover:shadow-xl transition-all duration-200"
    >
      <motion.div
        className="absolute inset-0 border-radius opacity-0 group-hover:opacity-10 transition-opacity duration-500 pointer-events-none"
        style={{
          background:
            "linear-gradient(135deg, transparent 0%, rgba(249, 115, 22, 0.1) 50%, transparent 100%)",
        }}
        initial={{ x: "-100%" }}
        whileHover={{ x: "100%" }}
        transition={{ duration: 0.1 }}
      />

      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={inView ? { scale: 1, rotate: 0 } : {}}
        transition={{
          delay: 0.5 + index * 0.2,
          type: "spring" as const,
          stiffness: 200,
          damping: 15,
        }}
        whileHover={{
          scale: 1.05,
          rotate: [0, -10, 10, -10, 0],
          transition: { duration: 0.2 },
        }}
        className="relative inline-flex items-center justify-center w-14 h-14 border-radius app-icon mb-6"
      >
        <motion.div
          className="absolute inset-0 border-radius bg-primary-400/20 blur-xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <div className="relative z-10">{icon}</div>
      </motion.div>

      <motion.h3
        initial={{ opacity: 0, x: -20 }}
        animate={inView ? { opacity: 1, x: 0 } : {}}
        transition={{ delay: 0.6 + index * 0.2 }}
        className="text-xl font-bold text-gray-900 mb-3"
      >
        {title}
      </motion.h3>

      <motion.p
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ delay: 0.7 + index * 0.2 }}
        className="text-gray-600 leading-relaxed text-base"
      >
        {description}
      </motion.p>

      <motion.div
        initial={{ scaleX: 0 }}
        whileHover={{ scaleX: 1 }}
        transition={{ duration: 0.3 }}
        className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-400 to-primary-500 rounded-b-2xl origin-left"
      />
    </motion.div>
  );
}
